import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { infertilityDetails } from "../../data/FemaleInfertilityDetail";
import Header from "../Header";
import Footer from "../Footer";

const FemaleInfertilityDetail = () => {
  const { slug } = useParams();
  const data = infertilityDetails.find((item) => item.slug === slug);

  const seoTitle =
    data?.seoTitle ||
    `${data?.title || "Female Infertility"} - Dr Meenu’s Clinic`;

  const seoDescription =
    data?.seoDescription ||
    `Learn about ${data?.title || "female infertility"} treatment at Dr Meenu’s Clinic.`;

  const seoKeywords =
    data?.seoKeywords ||
    `${data?.title || "female infertility"}, fertility clinic, Dr Meenu Clinic`;

  if (!data) {
    return (
      <>
        <Header />
        <section className="py-32 text-center bg-[#fcfaf8]">
          <h2 className="text-4xl font-serif text-[#b76e79] mb-6">
            Content Coming Soon
          </h2>
          <Link to="/female-infertility" className="text-[#b76e79]">
            ← Back
          </Link>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      {/* ================= HERO ================= */}
      <section className="relative h-[85vh] flex items-center justify-center text-center">
        <img
          src={data.heroImage}
          alt={data.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 max-w-4xl px-6 text-white">
          <h1 className="text-4xl md:text-6xl font-serif mb-6">
            {data.title}
          </h1>

          <div
            className="text-lg md:text-xl leading-relaxed opacity-90"
            dangerouslySetInnerHTML={{ __html: data.introduction }}
          />
        </div>
      </section>

      {/* ================= CONTENT ================= */}
      <section className="bg-[#fcfaf8] py-24 px-4 md:px-10">
        <div className="max-w-6xl mx-auto space-y-20">

          {/* PROCESS SECTION */}
          <div className="grid md:grid-cols-2 gap-12 items-center bg-white p-10 rounded-3xl shadow-xl border border-[#f0e4d7]">
            <img
              src={data.processImage}
              alt="Process"
              className="rounded-2xl shadow-lg w-full object-cover"
            />

            <div>
              <h2 className="text-3xl font-serif mb-6">
                {data.processTitle}
              </h2>

              <div className="space-y-6">
                {data.processSteps?.map((step, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="min-w-[45px] h-[45px] flex items-center justify-center rounded-full bg-[#b76e79] text-white font-semibold">
                      {index + 1}
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg">
                        {step.title}
                      </h3>
                      <p className="text-[#5a5a5a]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RECOMMENDED SECTION */}
          <div className="grid md:grid-cols-2 gap-12 items-center bg-white p-10 rounded-3xl shadow-xl border border-[#f0e4d7]">
            <div>
              <h2 className="text-3xl font-serif mb-6">
                {data.recommendedTitle}
              </h2>

              <ul className="space-y-4 text-[#5a5a5a]">
                {data.recommendedFor?.map((item, index) => (
                  <li key={index} className="border-b pb-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <img
              src={data.recommendedImage}
              alt="Recommended"
              className="rounded-2xl shadow-lg w-full object-cover"
            />
          </div>

          {/* BENEFITS SECTION */}
          <div className="grid md:grid-cols-2 gap-12 items-center bg-white p-10 rounded-3xl shadow-xl border border-[#f0e4d7]">
            <img
              src={data.benefitsImage}
              alt="Benefits"
              className="rounded-2xl shadow-lg w-full object-cover"
            />

            <div>
              <h2 className="text-3xl font-serif mb-6">
                {data.fourthheading}
              </h2>

              <ul className="space-y-3 text-[#5a5a5a]">
                {data.benefits?.map((benefit, index) => (
                  <li key={index}>• {benefit}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* EXPERTISE SECTION (only if available) */}
          {data.expertise && data.expertise.length > 0 && (
            <div className="bg-white p-10 rounded-3xl shadow-xl border border-[#f0e4d7]">
              <h2 className="text-3xl font-serif mb-6">
                Our Expertise
              </h2>

              <ul className="space-y-3 text-[#5a5a5a]">
                {data.expertise.map((point, index) => (
                  <li key={index}>• {point}</li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </>
  );
};

export default FemaleInfertilityDetail;